// Cross-component bridge for opening the global InsufficientBalanceModal.
// The popup state lives at EditorModals (the only React tree node guaranteed
// to outlive any child modal's remount), but several producers — image
// editor, image animator, sound modal, video modal — live deep inside
// modals that themselves unmount on close. They notify the popup via a
// window CustomEvent so the path doesn't depend on prop drilling.

export const INSUFFICIENT_BALANCE_EVENT = "aidea:insufficient-balance";

// The middleware refuses a paid operation for one of two reasons, both with
// the same body shape `{ message, details: { requiredCredits, availableCredits } }`:
//
//   "insufficient.balance"  — the ORGANIZATION balance does not cover it;
//   "team.budget_exceeded"  — the user's TEAM monthly budget does not
//                             (details also carry teamName and resetsAt).
//
// Both open the same popup. Every caller detects them through
// creditRefusalKind() below rather than its own string match: a dozen
// hand-written `includes("insufficient.balance")` checks are why a new refusal
// reached the screen as raw text inside the modal.
export const CREDIT_REFUSAL = Object.freeze({ BALANCE: "balance", TEAM: "team" });

const TEAM_BUDGET_TOKEN = "team.budget_exceeded";

/** Everything an error might carry its reason in, as one lowercase string. */
function refusalText(source) {
  if (source === null || source === undefined) return "";
  if (typeof source === "string") return source.toLowerCase();
  const parts = [source.message, source.error, source.details?.message, source.data?.message];
  try {
    parts.push(JSON.stringify(source));
  } catch {
    // circular — the explicit fields above are enough
  }
  return parts
    .filter((p) => typeof p === "string")
    .join(" ")
    .toLowerCase();
}

/**
 * Which credit refusal `source` is — an API error body, an Error, or a
 * message string — or null when it is some other error.
 */
export function creditRefusalKind(source) {
  const text = refusalText(source);
  if (text.includes(TEAM_BUDGET_TOKEN)) return CREDIT_REFUSAL.TEAM;
  if (text.includes("insufficient.balance") || text.includes("insufficient balance")) {
    return CREDIT_REFUSAL.BALANCE;
  }
  return null;
}

export const isCreditRefusal = (...sources) => sources.some((s) => creditRefusalKind(s) !== null);

/** The popup's closed state — the initial value of every holder's useState. */
export const CLOSED_CREDIT_REFUSAL = Object.freeze({
  show: false,
  kind: CREDIT_REFUSAL.BALANCE,
  requestedAmount: null,
  currentBalance: 0,
  teamName: null,
  resetsAt: null,
});

/**
 * The popup's open state for an error. `hint` is any extra text the caller
 * already has (e.g. a message string) that may name the reason when the
 * body does not.
 */
export function creditRefusalState(errData, hint) {
  const details = errData?.details || errData?.error?.details || {};
  const kind = creditRefusalKind(errData) || creditRefusalKind(hint) || CREDIT_REFUSAL.BALANCE;
  return {
    show: true,
    kind,
    requestedAmount:
      details.requiredCredits ??
      details.required ??
      errData?.requestedAmount ??
      errData?.requested ??
      null,
    currentBalance:
      details.availableCredits ??
      details.available ??
      errData?.currentBalance ??
      errData?.balance ??
      0,
    teamName: details.teamName ?? null,
    resetsAt: details.resetsAt ?? null,
  };
}

export function notifyInsufficientBalance(errData) {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(INSUFFICIENT_BALANCE_EVENT, { detail: errData || {} }));
  } catch (err) {
    console.warn("[insufficientBalance] dispatch failed", err);
  }
}
